import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { formatCpf, formatPhone } from '../../../utils/formatters'
import { findById } from '../../../services/tutors'
import { getPetByTutorId } from '../../../services/pet'
import * as S from './styles'
import SideMenu from '../components/SideMenu'
import BackButton from '../../../components/BackButton'
import Pagination from '../../../components/Pagination'
import { ReactComponent as SearchIcon } from '../../../assets/icons/search-orange.svg'
import { ReactComponent as UserAltOrangeIcon } from '../../../assets/icons/user-alt-orange.svg'
import { ReactComponent as PawOrangeIcon } from '../../../assets/icons/paw_circle.svg'
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg'
import { ReactComponent as GreenPenIcon } from '../../../assets/icons/green-pen.svg'
import Alert from '../../../components/Form/Alert'
import HeaderPage from '../components/HeaderPage'
import { alertConfig, LIMIT, INITIAL_CURRENT_PAGE } from './constants'
import NewPetModal from '../components/Modals/NewPetModal'
import NewTutorModal from '../components/Modals/NewTutorModal'
import SideMenuTemplate from '../templates/SideMenu'
import ResultBox from '../components/ResultBox'

const TutorDetails = () => {
  const { id: tutorId } = useParams()
  const alertParams = new URLSearchParams(useLocation().search).get('alertContent')
  const isShortcut = new URLSearchParams(useLocation().search).get('nova_consulta')
  const [isTutorModalVisible, setIsTutorModalVisible] = useState(false)
  const [isPetModalVisible, setIsPetModalVisible] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [currentPage, setCurrentPage] = useState(INITIAL_CURRENT_PAGE)
  const [isFirstRender, setIsFirstRender] = useState(true)
  const [loadingUserData, setLoadingUserData] = useState(false)
  const [loadingPetData, setLoadingPetData] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [tutor, setTutor] = useState()
  const [pets, setPets] = useState([])
  const [tutorWasUpdated, setTutorWasUpdated] = useState(false)
  const [petCreated, setPetCreated] = useState(false)
  const totalPets = pets?.total ?? 0
  const noPetsToShow = totalPets < 1
  const hasAlertToShow = alertParams || noPetsToShow
  const isLoading = loadingPetData || loadingUserData

  useEffect(() => {
    handleFetchData()
    setIsFirstRender(false)
  }, [])

  useEffect(() => {
    if (!isFirstRender) {
      fetchPetData(true)
    }
  }, [currentPage])

  useEffect(() => {
    if (loaded) {
      setShowAlert(hasAlertToShow)
    }
  }, [loaded, hasAlertToShow])

  useEffect(() => {
    if (tutorWasUpdated && loaded && !showAlert) {
      setShowAlert(true)
      handleFetchData()
    }
  }, [tutorWasUpdated])

  useEffect(() => {
    if (petCreated && loaded && !showAlert) {
      setShowAlert(true)
      handleFetchData()
      setIsPetModalVisible(false)
    }
  }, [petCreated])

  const fetchUserData = async () => {
    setLoadingUserData(true)
    const dataUser = await findById(tutorId)
    setTutor(dataUser)
    setLoadingUserData(false)
  }

  const fetchPetData = async () => {
    setLoadingPetData(true)
    const dataPet = await getPetByTutorId({ tutorId, limit: LIMIT, skip: (currentPage - 1) * LIMIT })
    setPets(dataPet)
    setLoadingPetData(false)
  }

  const handleFetchData = () => {
    try {
      fetchUserData()
      fetchPetData()
    } catch (error) {
      console.error(error)
    } finally {
      setLoaded(true)
    }
  }

  const tutorName = tutor?.lastName ? handleTutorFullName() : tutor?.firstName

  const handleCloseAlert = () => {
    setShowAlert(false)
  }

  const renderHeaderActions = () => {
    return <>
      <S.BoxButton>
        <S.EditButton onClick={() => {
          setShowAlert(false)
          setTutorWasUpdated(false)
          setPetCreated(false)
          setIsTutorModalVisible(true)
        }}><GreenPenIcon />Editar tutor</S.EditButton>
        <S.RegistrationButton onClick={() => {
          setShowAlert(false)
          setTutorWasUpdated(false)
          setPetCreated(false)
          setIsPetModalVisible(true)
        }}><PlusIcon />Cadastrar novo pet</S.RegistrationButton>
      </S.BoxButton>
    </>
  }

  function handleTutorFullName () {
    return tutor?.firstName && tutor?.lastName ? `${tutor?.firstName} ${tutor?.lastName}` : ''
  }

  function alertContent () {
    if (petCreated) { return 'PET_CREATED' }
    if (tutorWasUpdated) { return 'ACCOUNT_UPDATED' }
    if (alertParams) { return alertParams }
    if (noPetsToShow) { return 'NOT_PETS' }
  }

  const renderer = {
    shortcut: {
      headerProps: {
        icon: <PawOrangeIcon />,
        title: 'Selecione o pet deste atendimento',
        sub: 'Para prosseguir, selecione o pet que você vai atender'
      },
      title: tutorName ? `Pets de ${tutorName}` : '',
      titleStyle: 'bold'
    },
    default: {
      headerProps: {
        icon: <UserAltOrangeIcon />,
        title: tutorName,
        sub:
        [
          `CPF: ${formatCpf(tutor?.document) || 'Não preenchido'}`,
          `E-mail: ${tutor?.email || 'Não preenchido'}`,
          `Telefone: ${formatPhone(tutor?.homePhone) || 'Não preenchido'}`
        ],
        action: renderHeaderActions()
      },
      title: 'Pets do Tutor',
      titleStyle: 'tab'
    }
  }

  const payload = renderer[(!noPetsToShow && isShortcut) ? 'shortcut' : 'default']

  return <>
    <SideMenuTemplate>
      <S.MainContainer>
        <S.Main>
          {!(alertConfig[alertContent()]?.hideBackButton) && <BackButton colorButton={'#028BB7'} marginBottom={'0px'} noMargin marginButton={'0px'}>Voltar</BackButton>}
          <Alert type={alertConfig[alertContent()]?.icon} show={showAlert && !isLoading} onClose={handleCloseAlert}>
            {alertConfig[alertContent()]?.textContent}
          </Alert>
          <HeaderPage
            {...payload.headerProps}
            loading={loadingUserData}
          />
          <S.PetsWrapper>
            <S.WrapperHeader type={payload.titleStyle}>
              <span>{payload.title}</span>
            </S.WrapperHeader>
            <ResultBox
              totalResults={pets?.total}
              resultFor='pets'
              data={pets?.list}
              showItemsCount={false}
              loading={isLoading}
              limit={LIMIT}
              currentPage={currentPage}/>
            {!isLoading && noPetsToShow && (
              <S.Body>
                <S.IconWrapper>
                  <SearchIcon />
                </S.IconWrapper>
                <S.TextWrapper>
                  <S.TitlePets>Nenhum pet cadastrado no momento</S.TitlePets>
                  <S.SubtitlePets>Você pode cadastrar o pet deste tutor clicando em “Cadastrar novo pet”</S.SubtitlePets>
                </S.TextWrapper>
              </S.Body>
            )}
          </S.PetsWrapper>
          <Pagination
            totalItems={totalPets}
            itemsPerPage={LIMIT}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            show={!isLoading && totalPets > 0}
          />
        </S.Main>
      </S.MainContainer>
      <NewTutorModal visibility={isTutorModalVisible} setVisibility={setIsTutorModalVisible} tutorData={tutor} updated={setTutorWasUpdated} />
      <NewPetModal visibility={isPetModalVisible} setVisibility={setIsPetModalVisible} tutorData={tutor} updated={setTutorWasUpdated} alert={setPetCreated} />
    </SideMenuTemplate>
  </>
}

export default TutorDetails
